import React, { useMemo, useEffect, useCallback } from 'react';

import { SQUARE_FEET_PER_ACRE } from 'constants/unitTypes';
import useAuthContext from 'contexts/AuthContext';
import { useGetProFormaRoom } from 'queries/ProForma';
import { useGetDealById } from 'views/Deals/hooks/useGetDealById';
import { format } from 'date-fns';
import {
    AddressFields,
    CoordinateFields,
    DealOverviewData,
    DevelopmentTimelineData,
    Milestone,
    ExecutiveSummaryData,
    InvestmentMemoFormData,
    SelectedProFormaOption,
    SpecsData,
    TitleData,
    TeamData,
    UnitMixRentRollData,
} from 'views/InvestmentMemo/types';
import { calculateNetRentableArea } from 'views/ProFormaTable/helpers';
import { useMapCoordinates } from 'hooks/useMapCoordinates';
import { ITenant, IUnitMix } from 'views/ProFormaSetup/types';
import { ITenantDataCenter } from 'views/ProFormaTable/types';
import { IncomeCalculationType, ProFormaModelIds } from 'constants/proForma';
import { isApartmentModel, isApartmentWithRetailModel, isStudentHousingModel } from 'helpers/proForma';
import { formatDateToYearMonth } from 'ns_libs/formatter';
import { useDebounce } from 'hooks/useDebounce';
import useProFormaModels from 'queries/ProForma/useProFormaModels';
import { WattsType } from 'views/ProFormaTable/constants';
import { useDocumentSuggestions } from './useDocumentSuggestions';
import {
    DEFAULT_GROSS_RENT_TOTALS, DEFAULT_RENTAL_INCOME_TOTALS, MONTHS_IN_YEAR,
} from '../constants';
import {
    formatDevelopment,
    formatAssumptions,
    formatForecast,
    formatReturns,
    formatOperations,
} from '../helpers';

export const useInvestmentMemo = (
    selectedProFormaOption?: SelectedProFormaOption,
    formData?: InvestmentMemoFormData,
    setFormData?: React.Dispatch<React.SetStateAction<InvestmentMemoFormData>>,
) => {
    const { selectedOrganizationId, userOrganizations } = useAuthContext();

    const { id: proFormaId, dealId } = selectedProFormaOption?.value || {};
    const selectedUserOrganization = userOrganizations.find(org => org.organization.correlationId === selectedOrganizationId);

    const { data: deal, isLoading: isLoadingDeal } = useGetDealById({
        orgId: selectedOrganizationId!,
        dealId: Number(dealId),
        shouldFetch: !!Number(dealId),
    });

    const { data: proFormaRoom, isLoading: isLoadingProForma } = useGetProFormaRoom(
        dealId!,
        proFormaId!,
        selectedOrganizationId!,
        false,
        '',
        null,
        { enabled: !!dealId && !!proFormaId && !!selectedOrganizationId },
    );

    const { data: proFormaModels } = useProFormaModels();

    const generalInfo = proFormaRoom?.generalInfo;
    const usesSummary = proFormaRoom?.usesSummary;
    const operatingInfo = proFormaRoom?.operatingInfo;
    const permanentDebtInfo = proFormaRoom?.permanentDebtsInfo?.[0];
    const returnsData = proFormaRoom?.returns;
    const disposition = proFormaRoom?.disposition;
    const incomeSummary = proFormaRoom?.incomeSummary;
    const expenses = proFormaRoom?.expenseSummary;

    const organizationName = useMemo(() => {
        if (!selectedOrganizationId || !userOrganizations.length) return '';
        const selectedOrg = userOrganizations.find(
            org => org.organization.correlationId === selectedOrganizationId,
        );
        return selectedOrg?.organization.name || '';
    }, [selectedOrganizationId, userOrganizations]);

    const effectiveAddressFields: AddressFields = useMemo(() => {
        const proFormaGenInfo = proFormaRoom?.generalInfo;

        if (proFormaGenInfo?.address
            || proFormaGenInfo?.city
            || proFormaGenInfo?.state
            || proFormaGenInfo?.zipCode) {
            return {
                address: proFormaGenInfo?.address ?? '',
                city: proFormaGenInfo?.city ?? '',
                state: proFormaGenInfo?.state ?? '',
                zipCode: proFormaGenInfo?.zipCode ?? '',
            };
        }

        if (deal) {
            return {
                address: deal.address ?? '',
                city: deal.city ?? '',
                state: deal.state ?? '',
                zipCode: deal.zipCode ?? '',
            };
        }

        return {
            address: '',
            city: '',
            state: '',
            zipCode: '',
        };
    }, [deal, proFormaRoom]);

    const effectiveCoordinateFields: CoordinateFields = useMemo(() => {
        const proFormaGenInfo = proFormaRoom?.generalInfo;
        const proFormaHasAddress = !!(proFormaGenInfo?.address
            || proFormaGenInfo?.city
            || proFormaGenInfo?.state
            || proFormaGenInfo?.zipCode);
        const proFormaUsesCoords = !!proFormaGenInfo?.useCoordinates;
        // Treat lat/lng as an atomic pair so we never mix a pro-forma lat with a deal lng
        // (or vice versa) when one side only has half the coordinates filled in.
        const proFormaHasCoords = proFormaGenInfo?.latitude != null && proFormaGenInfo?.longitude != null;
        const dealHasCoords = deal?.latitude != null && deal?.longitude != null;

        const useCoordinates = proFormaUsesCoords || (!proFormaHasAddress && dealHasCoords);

        let latitude: number | null = null;
        let longitude: number | null = null;
        if (proFormaHasCoords) {
            latitude = proFormaGenInfo!.latitude!;
            longitude = proFormaGenInfo!.longitude!;
        } else if (useCoordinates && dealHasCoords) {
            latitude = deal!.latitude!;
            longitude = deal!.longitude!;
        }

        return { useCoordinates, latitude, longitude };
    }, [deal, proFormaRoom]);

    const titleData: TitleData = useMemo(() => ({
        dealName: deal?.dealName ?? '',
        address: effectiveAddressFields?.address ?? '',
        city: effectiveAddressFields?.city ?? '',
        state: effectiveAddressFields?.state ?? '',
        zipCode: effectiveAddressFields?.zipCode ?? '',
        sponsor: selectedUserOrganization?.organization.name ?? '',
        date: format(new Date().toISOString(), 'yyyy-MM'),
        associatedUniversity: generalInfo?.associatedUniversity ?? '',
        tagline: '',
        logoFiles: selectedUserOrganization?.organization.logoUrl
            ? [{
                id: 'default-logo',
                objectKey: 'logo',
                bucketName: 'default-bucket',
                url: selectedUserOrganization.organization.logoUrl,
                name: 'default logo',
                uid: 'default-logo',
                status: 'done',
            }]
            : [],
        buildingExteriorFiles: {
            uploaded: [],
            suggested: [],
        },
        useCoordinates: effectiveCoordinateFields.useCoordinates,
        latitude: effectiveCoordinateFields.latitude,
        longitude: effectiveCoordinateFields.longitude,
    }), [
        deal,
        selectedUserOrganization,
        effectiveAddressFields,
        effectiveCoordinateFields,
        generalInfo?.associatedUniversity,
    ]);

    const totalEquityRequired = proFormaRoom?.waterfallAnalysis?.contributions?.totalAmount;

    const executiveSummaryData: ExecutiveSummaryData = useMemo(() => ({
        opportunityDescription: '',
        totalCapitalization: proFormaRoom?.sourcesSummary?.totals.amount ? String(Math.round(Number(proFormaRoom.sourcesSummary.totals.amount))) : '',
        projectedLpIrr: (Number(proFormaRoom?.waterfallAnalysis?.waterfallSummary?.lpIrr || 0) * 100).toFixed(2),
        avgCashOnCash: (Number(proFormaRoom?.permanentDebtsInfo?.[0]?.cashOnCashReturn || 0) * 100).toFixed(2),
        holdPeriod: proFormaRoom?.generalInfo?.holdPeriod ? (proFormaRoom.generalInfo.holdPeriod / MONTHS_IN_YEAR).toFixed(2) : '',
        totalEquityRequired: totalEquityRequired ? String(Math.round(Number(totalEquityRequired))) : '',
        projectedEquityMultiple: proFormaRoom?.waterfallAnalysis?.waterfallSummary?.lpEmx ? 
            Number(proFormaRoom.waterfallAnalysis.waterfallSummary.lpEmx).toFixed(2) : '',
        buildPeriod: proFormaRoom?.generalInfo?.constructionDuration ? Number(proFormaRoom.generalInfo.constructionDuration).toFixed(2) : '',
    }), [proFormaRoom, totalEquityRequired]);

    const totalGrossBuildableArea = proFormaRoom?.buildableLots?.totalGrossBuildableArea || 0;
    const siteAreaAcres = parseFloat((totalGrossBuildableArea / SQUARE_FEET_PER_ACRE).toFixed(1));

    const netRentableArea = generalInfo?.proFormaModelId ? calculateNetRentableArea(
        generalInfo.proFormaModelId,
        Number(generalInfo.builtGrossSquareFootage) || 0,
        Number(generalInfo.efficiencyRatio) || 0,
        proFormaRoom?.incomeSummary || {},
        Number(generalInfo.retailBuiltGrossSquareFootage) || 0,
        Number(generalInfo.retailEfficiencyRatio) || 0,
    ) : 0;

    const buildingSizeGrossSf = (Number(generalInfo?.builtGrossSquareFootage) || 0)
        + (generalInfo?.proFormaModelId && isApartmentWithRetailModel(generalInfo.proFormaModelId)
            ? (Number(generalInfo.retailBuiltGrossSquareFootage) || 0)
            : 0);

    const proFormaAssetType = useMemo(() => {
        if (!generalInfo?.proFormaModelId || !proFormaModels?.length) return undefined;
        return proFormaModels.find(({ id }) => id === generalInfo.proFormaModelId)?.name;
    }, [generalInfo?.proFormaModelId, proFormaModels]);

    const specs: SpecsData = useMemo(() => ({
        assetType: proFormaAssetType ?? deal?.assetType?.name,
        siteArea: siteAreaAcres,
        buildingSizeGrossSf,
        buildingSizeNetRentableSf: netRentableArea,
        parkingSpaces: {
            structured: generalInfo?.structuredParkingSpaces ?? null,
            surface: generalInfo?.surfaceParkingSpaces ?? null,
            underground: generalInfo?.undergroundParkingSpaces ?? null,
            trailer: generalInfo?.trailerParkingSpaces ?? null,
        },
        proFormaModelId: generalInfo?.proFormaModelId,
        totalUnits: proFormaRoom?.incomeSummary?.totalUnits,
    }), [
        deal,
        proFormaAssetType,
        siteAreaAcres,
        generalInfo,
        netRentableArea,
        buildingSizeGrossSf,
        proFormaRoom?.incomeSummary?.totalUnits,
    ]);

    const mapUnitMixFromBE = (unitMixFromBE: any[]): IUnitMix[] => unitMixFromBE.map(item => ({
        id: item.id,
        name: item.name,
        units: item.units,
        averageSquareFootage: item.averageSf,
        valuePerUnitPerMonth: item.pricePerUnit,
        amountPerYear: item.amountPerYear,
    }));
    const formattedUnitMix: IUnitMix[] = useMemo(
        () => (proFormaRoom?.incomeSummary?.unitMix ? mapUnitMixFromBE(proFormaRoom.incomeSummary.unitMix) : []),
        [proFormaRoom?.incomeSummary?.unitMix],
    );

    const unitMixTotals = proFormaRoom?.incomeSummary?.grossRent;

    const isDataCenter = generalInfo?.proFormaModelId === ProFormaModelIds.DATA_CENTER;

    const tenantsDataCenter: ITenantDataCenter[] = useMemo(
        () => (isDataCenter
            ? (proFormaRoom?.incomeSummary?.incomeTenantsDataCenter ?? [])
            : []),
        [isDataCenter, proFormaRoom?.incomeSummary?.incomeTenantsDataCenter],
    );

    const tenants: ITenant[] = useMemo(
        () => (!isDataCenter
            ? (proFormaRoom?.incomeSummary?.incomeTenants ?? []).map((tenant: any) => ({
                ...tenant,
                leaseStart: tenant.leaseStart === null ? undefined : tenant.leaseStart,
            }))
            : []),
        [isDataCenter, proFormaRoom?.incomeSummary?.incomeTenants],
    );

    const tenantsTotals = proFormaRoom?.incomeSummary?.totalRentalIncome;
    const retailTotals = proFormaRoom?.incomeSummary?.totalRetailIncome;

    const estimatesDataCenter: ITenantDataCenter[] = useMemo(
        () => (isDataCenter
            ? (proFormaRoom?.incomeSummary?.incomeFloorEstimatesDataCenter ?? []).map((estimate: any) => ({
                ...estimate,
                tenant: `Floor ${estimate.floor} - ${(Number(estimate.allocationPercentage) * 100).toFixed(0)}%`,
            }))
            : []),
        [isDataCenter, proFormaRoom?.incomeSummary?.incomeFloorEstimatesDataCenter],
    );

    const estimates: ITenant[] = useMemo(
        () => (!isDataCenter
            ? (proFormaRoom?.incomeSummary?.incomeFloorEstimates ?? []).map((estimate: any) => ({
                id: estimate.id,
                tenant: `Floor ${estimate.floor} - ${(Number(estimate.allocationPercentage) * 100).toFixed(0)}%`,
                squareFootage: estimate.squareFootage,
                valuePerSfPerYear: estimate.valuePerSfPerYear,
                annualRent: estimate.annualRent,
            }))
            : []),
        [isDataCenter, proFormaRoom?.incomeSummary?.incomeFloorEstimates],
    );

    const isTenant: boolean = proFormaRoom?.operatingInfo?.incomeCalculationType === IncomeCalculationType.TENANTS;

    const unitMixRentRoll: UnitMixRentRollData | null = useMemo(() => {
        if (!generalInfo?.proFormaModelId) return null;
        if (isApartmentWithRetailModel(generalInfo.proFormaModelId)) {
            return {
                data: {
                    type: 'unit-mix-rent-roll',
                    unitMix: {
                        value: formattedUnitMix,
                        totals: unitMixTotals ?? DEFAULT_GROSS_RENT_TOTALS,
                    },
                    rentRoll: {
                        value: isTenant ? tenants : estimates,
                        totals: retailTotals ?? DEFAULT_RENTAL_INCOME_TOTALS,
                    },
                },
            };
        }
        if (isApartmentModel(generalInfo.proFormaModelId)) {
            return {
                data: {
                    type: 'unit-mix',
                    value: formattedUnitMix,
                    totals: unitMixTotals ?? DEFAULT_GROSS_RENT_TOTALS,
                },
            };
        }
        if (isStudentHousingModel(generalInfo.proFormaModelId)) {
            return {
                data: {
                    type: 'bed-mix',
                    value: proFormaRoom?.incomeSummary?.bedMix ?? [],
                    totals: unitMixTotals ?? DEFAULT_GROSS_RENT_TOTALS,
                },
            };
        }
        if (isDataCenter) {
            return {
                data: {
                    type: 'rent-roll',
                    value: isTenant ? tenantsDataCenter : estimatesDataCenter,
                    totals: tenantsTotals ?? DEFAULT_RENTAL_INCOME_TOTALS,
                    isDataCenter: true,
                },
            };
        }
        return {
            data: {
                type: 'rent-roll',
                value: isTenant ? tenants : estimates,
                totals: tenantsTotals ?? DEFAULT_RENTAL_INCOME_TOTALS,
                isDataCenter: false,
            },
        };
    }, [
        generalInfo?.proFormaModelId,
        formattedUnitMix,
        unitMixTotals,
        tenants,
        tenantsDataCenter,
        estimatesDataCenter,
        estimates,
        tenantsTotals,
        retailTotals,
        isTenant,
        isDataCenter,
        proFormaRoom?.incomeSummary?.bedMix,
    ]);

    const dealOverview: DealOverviewData = useMemo(() => ({
        siteStackingPlanFiles: {
            uploaded: [],
            suggested: [],
        },
        specs,
        unitMixRentRoll,
    }), [specs, unitMixRentRoll]);

    const {
        data: documentSuggestions,
        isLoading: isLoadingDocumentSuggestions,
    } = useDocumentSuggestions({
        dealId,
    });

    const team: TeamData = useMemo(() => ({
        sponsorCompanyName: organizationName,
        sponsorBio: '',
        assetsUnderManagement: '',
        principals: [],
        vendors: {
            legal: deal?.legalServices?.id,
            architect: deal?.projectArchitect?.id,
            engineer: undefined,
            surveyor: deal?.surveyor?.id,
            environmentalConsultant: deal?.environmentalConsultant?.id,
        },
    }), [deal, organizationName]);

    const getConstructionEndDate = useCallback(() => {
        if (!generalInfo?.constructionStart || !generalInfo?.constructionDuration) return null;

        const startDate = new Date(generalInfo.constructionStart);
        startDate.setMonth(startDate.getMonth() + generalInfo.constructionDuration);
        return startDate.toISOString();
    }, [generalInfo?.constructionStart, generalInfo?.constructionDuration]);

    const developmentTimeline: DevelopmentTimelineData = useMemo(() => ({
        milestones: [
            { id: 'acquisition', date: formatDateToYearMonth(generalInfo?.startDate ?? null), name: 'Acquisition' },
            { id: 'construction-start', date: formatDateToYearMonth(generalInfo?.constructionStart ?? null), name: 'Construction start' },
            { id: 'construction-end', date: formatDateToYearMonth(getConstructionEndDate()), name: 'Construction end' },
            { id: 'operation-start', date: formatDateToYearMonth(operatingInfo?.operationStartDate ?? null), name: 'Operation start' },
            { id: 'stabilization', date: formatDateToYearMonth(permanentDebtInfo?.loanFundingDate ?? null), name: 'Stabilization' },
            { id: 'exit', date: formatDateToYearMonth(generalInfo?.saleDate ?? null), name: 'Exit' },
        ].filter((m): m is Milestone => m.date !== null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [generalInfo, usesSummary, operatingInfo, permanentDebtInfo]);

    const wattsType = WattsType.KILOWATTS;

    const formattedDevelopment = useMemo(
        () => (generalInfo?.proFormaModelId
            ? formatDevelopment(generalInfo.proFormaModelId, proFormaRoom?.usesSummary, proFormaRoom?.sourcesSummary)
            : []),
        [generalInfo?.proFormaModelId, proFormaRoom?.usesSummary, proFormaRoom?.sourcesSummary],
    );

    const formattedAssumptions = useMemo(
        () => (generalInfo?.proFormaModelId && incomeSummary && operatingInfo && expenses
            ? formatAssumptions(
                generalInfo.proFormaModelId,
                incomeSummary,
                operatingInfo,
                expenses,
                wattsType,
            )
            : []),
        [generalInfo?.proFormaModelId, incomeSummary, operatingInfo, expenses, wattsType],
    );

    const formattedForecast = useMemo(
        () => (disposition && operatingInfo && permanentDebtInfo && returnsData
            ? formatForecast(disposition, operatingInfo, permanentDebtInfo, returnsData)
            : null),
        [disposition, operatingInfo, permanentDebtInfo, returnsData],
    );

    const formattedOperations = useMemo(
        () => (disposition && generalInfo?.proFormaModelId && operatingInfo
            ? formatOperations(
                generalInfo.proFormaModelId,
                disposition,
                operatingInfo?.incomeCalculationType as IncomeCalculationType,
                wattsType,
            )
            : null),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [generalInfo?.proFormaModelId, disposition, operatingInfo?.incomeCalculationType, wattsType],
    );

    const formattedReturns = useMemo(
        () => (returnsData && Object.keys(returnsData).length && proFormaRoom?.waterfallAnalysis
            ? formatReturns(returnsData, proFormaRoom.waterfallAnalysis)
            : null),
        [returnsData, proFormaRoom?.waterfallAnalysis],
    );

    const debouncedTitleData = useDebounce(formData?.title, 1000);
    const { lat, lng } = useMapCoordinates(
        debouncedTitleData
            ? {
                longitude: debouncedTitleData.longitude ?? undefined,
                latitude: debouncedTitleData.latitude ?? undefined,
                address: debouncedTitleData.address,
                city: debouncedTitleData.city,
                state: debouncedTitleData.state,
                zipCode: debouncedTitleData.zipCode,
                useCoordinates: debouncedTitleData.useCoordinates,
            }
            : null,
    );

    useEffect(() => {
        if (lat == null || lng == null || !setFormData) return;

        setFormData(prev => {
            const prevLat = prev.location?.coordinates?.lat;
            const prevLng = prev.location?.coordinates?.lng;
            if (prevLat === lat && prevLng === lng) {
                return prev;
            }

            return {
                ...prev,
                location: {
                    ...prev.location,
                    coordinates: {
                        lat,
                        lng,
                    },
                },
            };
        });
    }, [lat, lng, setFormData]);

    useEffect(() => {
        if (!setFormData) return;
        if (!selectedProFormaOption?.value || selectedProFormaOption.value.type !== 'proforma') return;
        if (isLoadingProForma) return;

        setFormData(prev => ({
            ...prev,
            title: {
                ...prev.title,
                ...(titleData || {}),
                // Preserve AI-generated tagline if it exists
                tagline: prev.title.tagline || titleData?.tagline || '',
            },
            executiveSummary: {
                ...prev.executiveSummary,
                ...(executiveSummaryData || {}),
                // Preserve AI-generated opportunity description if it exists
                opportunityDescription: prev.executiveSummary.opportunityDescription || executiveSummaryData?.opportunityDescription || '',
            },
            dealOverview: dealOverview ? { ...prev.dealOverview, ...dealOverview } : prev.dealOverview,
            team: team?.sponsorCompanyName ? { ...prev.team, ...team } : prev.team,
            developmentTimeline: developmentTimeline?.milestones?.length ? developmentTimeline : prev.developmentTimeline,
            financials: {
                ...prev.financials,
                development: formattedDevelopment,
                assumptions: formattedAssumptions,
                forecast: formattedForecast,
                operations: formattedOperations,
                returns: formattedReturns,
            },
        }));
    }, [
        setFormData,
        selectedProFormaOption,
        isLoadingProForma,
        titleData,
        executiveSummaryData,
        dealOverview,
        team,
        developmentTimeline,
        formattedDevelopment,
        formattedAssumptions,
        formattedForecast,
        formattedOperations,
        formattedReturns,
    ]);

    return {
        dealOverview,
        titleData,
        executiveSummaryData,
        team,
        developmentTimeline,
        proFormaRoom,
        documentSuggestions,
        isTitleAddressLoaded: !isLoadingProForma && !isLoadingDeal,
        isLoadingProForma,
        isLoadingDocumentSuggestions,
    };
};
